import { createIndexFromTree, transformTree } from '@futrli/shared-services';
import { ConsolidatedForecastReport, ConsolidatedForecastReportStructure } from '../../../../../core/types';
import { ForecastTreeNode } from '../../types';

/**
 * Returns the combined forecast trees for the given forecast report
 * @param forecastReport - The forecast report to process and extract the data from
 */
export const getCombinedForecastTrees = (forecastReport: ConsolidatedForecastReport | null | undefined) => {
  const output: Record<string, { tree: ForecastTreeNode[]; index: Record<string, ForecastTreeNode> }> = {};

  if (forecastReport) {
    const structureKeys = Object.keys(forecastReport.structure) as (keyof ConsolidatedForecastReportStructure)[];
    structureKeys.forEach(key => {
      output[key] = getCombinedForecastTree(forecastReport, key);
    });
  }

  return output;
};

/**
 * Returns the requested tree structure and node index, with all nodes fully populated with data
 * @param forecastReport - The forecast report to extract the data from
 * @param structure - The structure to extract and populate
 */
export const getCombinedForecastTree = (
  forecastReport: ConsolidatedForecastReport | null | undefined,
  structure: keyof ConsolidatedForecastReportStructure
) => {
  const tree: ForecastTreeNode[] = forecastReport
    ? transformTree(forecastReport.structure[structure], (n, level, parent, parentTree) => {
        return {
          ...n,
          ...forecastReport.data.by_ref[n.ref],
          ...forecastReport.node_info.by_ref[n.ref],
          childCount: n.children?.length || 0,
          children: [], // transformTree fixes this for us, this is here to keep type checking happy
          isFirst: parentTree[0].ref === n.ref,
          isLast: parentTree[parentTree.length - 1].ref === n.ref,
          is_unused: !!forecastReport.data.by_ref[n.ref]?.is_unused,
          level,
          parentRef: parent?.ref,
          siblingCount: parent ? (parent.children || []).length : parentTree.length,
          siblingIndex: parent ? (parent.children || []).indexOf(n) : 0
        };
      })
    : [];
  const index = forecastReport ? createIndexFromTree(tree, { idProp: 'ref' }) : {};
  return { tree, index };
};

/**
 * Recursively sorts the children of each node that are assumptions/predictions alphabetically by name
 * @param nodes - The tree nodes to sort
 */
export const sortPredictionChildren = (nodes: ForecastTreeNode[]): ForecastTreeNode[] => {
  return nodes.map(node => {
    if (node.children?.length) {
      const sortedChildren = [...node.children].sort((a, b) => {
        if (
          (a.type === 'assumption' || a.type === 'system-assumption') &&
          (b.type === 'assumption' || b.type === 'system-assumption')
        ) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return 0;
      });
      return { ...node, children: sortPredictionChildren(sortedChildren) };
    }
    return node;
  });
};

/**
 * Returns true if this node is not considered to be unused
 * @param treeNode - The tree node to check
 */
export const isUnusedNode = (treeNode: ForecastTreeNode) => {
  return treeNode.is_unused;
};
